import { findKnownShortcodes } from '@shared/functions/find-short-codes';
import { Contract } from '@shared/models/contract';
import { Person } from '@shared/models/person';
import { Company } from '@shared/models/company';
import { Boat, distinctBerths } from '@shared/models/boat';
import { Berth } from '@shared/models/berth';
import { Tenant } from '@shared/models/tenant';
import { BcmBooking } from '@shared/models/bcm-booking';
import { missingContractFieldsPerson } from '@bcm-work-flows/contract-creator/missing-contract-fields-person';
import { missingContractFieldsCompany } from '@bcm-work-flows/contract-creator/missing-contract-fields-company';
import { resolveContractBerthAssignments } from '@bcm-work-flows/contract-creator/contract-creator-berths.util';

export interface ContractMissingFieldsSnapshot {
    tenant: string[];
    person?: string[];
    company?: string[];
    boat: string[];
    berth: string[];
}

export function filterContractsForPreflight(
    contracts: Contract[],
    tenantRelationIds: number[],
): Contract[] {
    if (!contracts.length) {
        return [];
    }

    const matching = contracts.filter((contract) =>
        (contract.tenantRelations || []).some((relation) => tenantRelationIds.includes(relation.id)),
    );

    return matching.length ? matching : contracts;
}

export function hasAnyContractMissingFields(fields: ContractMissingFieldsSnapshot): boolean {
    return (fields.tenant?.length ?? 0)
        + (fields.person?.length ?? 0)
        + (fields.company?.length ?? 0)
        + (fields.boat?.length ?? 0)
        + (fields.berth?.length ?? 0) > 0;
}

/** Keeps, per bucket, only the entries every snapshot reports. */
function intersectMissingFields(snapshots: ContractMissingFieldsSnapshot[]): ContractMissingFieldsSnapshot {
    const [first, ...rest] = snapshots;
    const result = { ...first };

    (Object.keys(first) as (keyof ContractMissingFieldsSnapshot)[]).forEach((key) => {
        result[key] = rest.reduce(
            (common, snapshot) => common.filter((item) => (snapshot[key] ?? []).includes(item)),
            first[key] ?? [],
        );
    });

    return result;
}

export function computeContractPreflightMissingFields(input: {
    tenant: Tenant;
    contracts: Contract[];
    person?: Person;
    company?: Company;
    boat?: Boat;
    booking?: BcmBooking;
}): ContractMissingFieldsSnapshot | null {
    const { person, company, tenant, contracts, boat: explicitBoat, booking } = input;

    if (!person && !company) {
        return null;
    }

    const tenantRelationIds = ((company ?? person).activeAndNextTenantRelationAssignments ?? [])
        .map((assignment) => assignment.tenantRelation.id);

    const relevantContracts = filterContractsForPreflight(contracts, tenantRelationIds);

    if (!relevantContracts.length) {
        return null;
    }

    // Same candidates the wizard offers - the boat and berth are only picked there.
    const boatCandidates = explicitBoat ? [explicitBoat] : ((company ? company.boats : person.boats) ?? []);
    const boats: Boat[] = boatCandidates.length ? boatCandidates : [undefined];
    const berthCandidates = distinctBerths(boats.flatMap((boat) => resolveContractBerthAssignments(booking, boat)));
    const berths: Berth[] = berthCandidates.length ? berthCandidates : [undefined];

    // Neither the template nor the boat/berth is chosen yet: only report what is missing whichever
    // of them the user picks, so the dialog never sends them to fix data that is there. (#7052)
    const snapshots = relevantContracts.flatMap((contract) => {
        const knownShortCodeList = findKnownShortcodes(contract?.content ?? '');

        return boats.flatMap((boat) => berths.map((berth) => person
            ? missingContractFieldsPerson(knownShortCodeList, tenant, person, boat, berth)
            : missingContractFieldsCompany(knownShortCodeList, tenant, company, boat, berth)));
    });

    return intersectMissingFields(snapshots);
}
